import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "api/baseurl";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// @mui material components
import Card from "@mui/material/Card";
import Switch from "@mui/material/Switch";
import CircularProgress from "@mui/material/CircularProgress"; // ✅ loading spinner
import Grid from "@mui/material/Grid";
import MuiLink from "@mui/material/Link";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";

// Authentication layout components
import BasicLayout from "layouts/authentication/components/BasicLayout";

// Encryption and Decryption Functions
import { encryptBody } from "encryptionAndDecryption/bodyEncryption";
import { decryptBody } from "encryptionAndDecryption/bodyDecryption";
import { fetchSecretKey } from "encryptionAndDecryption/secretKeyDecryption";

// Images
import bgImage from "images/Background.png";

function Basic() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false); // ✅ for spinner
  const navigate = useNavigate();

  const handleSetRememberMe = () => setRememberMe(!rememberMe);

 const handleLogin = async (e) => {
  e.preventDefault();
  setError("");
  setLoading(true); // start loading

  try {
    // Fetch secret key
    const secretKey = await fetchSecretKey();

    // Encrypt login credentials
    const encryptedLoginData = encryptBody({ username, password }, secretKey);

    // Send encrypted payload
    const response = await axios.post("/api/authenticate", encryptedLoginData, {
      headers: { "Content-Type": "text/plain" },
    });

    // Decrypt response
    const encryptedResponse = response.data;
    const decryptedResponse = decryptBody(encryptedResponse, secretKey);
    console.log("decryptedResponse", decryptedResponse);

    if (!decryptedResponse) {
      setError("Invalid login credentials.");
      toast.error("Invalid login credentials.");
      return;
    }

    // Save login data in localStorage
    localStorage.setItem("loginData", JSON.stringify(decryptedResponse));

    // ✅ FIRST LOGIN CHECK (for all roles except super admin)
    if (decryptedResponse.status === "FIRST_LOGIN") {
      toast.info(decryptedResponse.message || "Please reset your password.");
      // Save username to localStorage for reset password
      localStorage.setItem(
        "loginData",
        JSON.stringify({ username: decryptedResponse.username })
      );
      navigate(decryptedResponse.redirectUrl || "/resetpassword");
      return; // stop further execution
    }

   if (
        decryptedResponse.role !== "ROLE_SUPER" &&
        (decryptedResponse.status === "PRIVACY_NOT_ACCEPTED" ||
          decryptedResponse.privacyAccepted === false)
      ) {
        toast.info("Please accept the Privacy Policy to continue.");
        localStorage.setItem(
          "loginData",
          JSON.stringify({
            userid: decryptedResponse.userid,
            username: decryptedResponse.username,
            token: decryptedResponse.token,
            role: decryptedResponse.role,
          })
        );
        navigate("/privacypolicy");
        return;
      }



    // ✅ Normal login flow - After all checks pass
    if (decryptedResponse.username === username) {
      toast.success("Login successful!");
     const storedLoginData = JSON.parse(localStorage.getItem("loginData"));
  const role = storedLoginData?.role;

  if (role === "ROLE_SUPER") {
    navigate("/SuperDashboard");
  } else {
    navigate("/dashboard");
  }
      window.location.reload();
    } else {
      toast.error("Invalid Credentials");
    }

  } catch (err) {
    const serverMessage =
      err.response?.data?.message || "Login failed. Please try again.";
    setError(serverMessage);
    toast.error(serverMessage);
    console.error("Login error:", err);
  } finally {
    setLoading(false); // stop loading
  }
};


  function cynefianwebsite(e) {
    window.open("https://www.cynefian.com", "_blank");
  }

  return (
    <BasicLayout image={bgImage}>
      <Card>
        <MDBox pt={8} pb={3} px={3}>
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
            <img
              className="mb-3"
              src="/carepro.jpg"
              alt="logo"
              style={{ height: "70px", marginTop: "-5px" }}
            />
          </div>


          <MDBox component="form" role="form" onSubmit={handleLogin}>
            <MDBox mb={2} style={{ marginTop: "20px" }}>
              <MDInput
                type="email"
                label="Email"
                fullWidth
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
              />
            </MDBox>

            <MDBox mb={2}>
              <MDInput
                type="password"
                label="Password"
                fullWidth
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </MDBox>

            <MDBox textAlign="right" mb={2}>
              <MDTypography
                component={Link}
                to="/forgotpassword"
                variant="caption"
                color="info"
                fontWeight="medium"
                sx={{ cursor: "pointer", textDecoration: "none" }}
              >
                Forgot Password?
              </MDTypography>
            </MDBox>

            <MDBox display="flex" alignItems="center" ml={-1}>
              <Switch checked={rememberMe} onChange={handleSetRememberMe} />
              <MDTypography
                variant="button"
                fontWeight="regular"
                color="text"
                onClick={handleSetRememberMe}
                sx={{ cursor: "pointer", userSelect: "none", ml: -1 }}
              >
                &nbsp;&nbsp;Remember me
              </MDTypography>
            </MDBox>

            {error && (
              <MDBox mt={2}>
                <MDTypography variant="caption" color="error">
                  {error}
                </MDTypography>
              </MDBox>
            )}

            <MDBox mt={4} mb={1}>
              <MDButton
                type="submit"
                variant="gradient"
                color="info"
                fullWidth
                disabled={loading} // ✅ disable button when loading
              >
                {loading ? (
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "8px",
                    }}
                  >
                    <CircularProgress
                      size={20}
                      color="inherit"
                      thickness={4}
                    />{" "}
                    Logging in...
                  </span>
                ) : (
                  "Login"
                )}
              </MDButton>
            </MDBox>
          </MDBox>
        </MDBox>
      </Card>
    </BasicLayout>
  );
}

export default Basic;
